import React, { useEffect, useRef, useState } from 'react';
import './SearchBar.css';
import SearchIcon from '../../assets/SearchIcon.jsx';
import BackArrow from '../../assets/BackArrow.jsx';
import CloseIcon from '../../assets/CloseIcon.jsx';
import PropTypes from 'prop-types';

/**
 * @param {object} props - the props of the component
 * @param {string} props.defaultText - the default text to display
 * @param {string} [props.value] - the value of the input
 * @param {Function} [props.onChange] - the change handler
 * @param {Function} [props.onClose] - callback when search is closed via back arrow
 * @param {Function} [props.onFocusChange] - callback when focus state changes
 * @returns {JSX.Element} SearchBar Component
 */
export default function SearchBar({ defaultText, value, onChange, onClose, onFocusChange }) {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);

  // When the parent clears the value externally, reset focus state and blur the input
  useEffect(() => {
    if (!value && isFocused) {
      setIsFocused(false);
      inputRef.current?.blur();
    }
  }, [value]);

  const updateFocus = focused => {
    setIsFocused(focused);
    onFocusChange?.(focused);
  };

  const handleBack = () => {
    onChange?.('');
    updateFocus(false);
    inputRef.current?.blur();
    onClose?.();
  };

  const handleClear = () => {
    onChange?.('');
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    updateFocus(true);
  };

  return (
    <form
      className={ `search-bar ${isFocused ? 'search-bar--focused' : ''}` }
      onSubmit={ e => e.preventDefault() }
    >
      <button
        type='button'
        className='search-bar-left-btn'
        onClick={ isFocused ? handleBack : undefined }
        aria-label={ isFocused ? 'Back' : 'Search' }
      >
        { isFocused ? <BackArrow /> : <SearchIcon /> }
      </button>
      <input
        ref={ inputRef }
        type='text'
        placeholder={ defaultText }
        value={ value }
        onChange={ e => onChange?.(e.target.value) }
        onFocus={ handleFocus }
      />
      {
        isFocused && value && (
          <button
            type='button'
            className='search-bar-close-btn'
            onClick={ handleClear }
            aria-label='Clear search'
          >
            <CloseIcon />
          </button>
        )
      }
    </form>
  );
}

SearchBar.propTypes = {
  defaultText: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  onFocusChange: PropTypes.func,
};
